import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Divider,
  Paper,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Table,
} from "@mui/material";
import { useSelector } from "react-redux";
import moment from "moment";
import { getLocationName } from "../../utils/mealUtils";
import { mealDataByLocation } from "../../Data/data";
import InvoiceDinnerByLocation from "./InvoiceDinnerByLocation";
import InvoiceBreakFastByLocation from "./InvoiceBreakFastByLocation";

const InvoiceSummary = ({
  invoiceSummaryData,
  setInvoiceSummaryData,
  // setInvoiceDetailsByLocation,
  invoiceDescriptionData,
  vendorIdByLocation,
  vendorId,
  chargedPlateCount,
  totalInvoice,
  ApiStatus,
  startDate,
  mealType,
  perPlatePrice,
  minGuarantee,
  location,
}) => {
  const [lessConsumptionDays, setLessConsumptionDays] = useState(0);
  const [moreConsumptionDays, setMoreConsumptionDays] = useState(0);

  //using the following variable to setStartDate initially when startDate is null
  let invoiceDate =
    startDate === null
      ? invoicePeriod(
          moment(new Date()).subtract(1, "month").format("MMMM"),
          moment(new Date()).format("YYYY")
        )
      : invoicePeriod(moment(startDate?.$d).format("MMMM"), startDate?.$y);

  const getUniqueMealData = useCallback(() => {
    if (invoiceSummaryData) {
      let uniqueMealData = [];
      let uniqueKey = new Set();
      invoiceSummaryData.vendorId !== 1 &&
        invoiceSummaryData?.invoiceSummary?.forEach((item) => {
          // if (!uniqueKey.has(item.diet_name)) {
          uniqueKey.add(item.diet_name);
          if (!uniqueKey.has(item.days_name)) {
            uniqueKey.add(item.days_name);
            uniqueMealData.push(item);
          }
          // }
        });
      return uniqueMealData;
    }
  }, [invoiceSummaryData]);

  //using the following object to set the key values pair of the invoice summary data
  const details = useMemo(() => {
    let serviceProvider = invoiceSummaryData?.vendorName
      ? invoiceSummaryData?.vendorName
      : invoiceSummaryData?.invoiceVendorName;
    return {
      "Invoice Period": invoiceDate,
      "Service provider": handleTextTransform(serviceProvider),
      "Per plate cost": `₹ ${perPlatePrice}`,
      "Number of Min guarantee": minGuarantee,
      costAndMinGurantee: getUniqueMealData(),
      // invoicePerPlatePrice: perPlatePrice,
    };
  }, [
    getUniqueMealData,
    invoiceDate,
    invoiceSummaryData?.invoiceVendorName,
    invoiceSummaryData?.vendorName,
    minGuarantee,
    perPlatePrice,
  ]);

  //final Obj
  let invoiceDetails = {
    title: `${
      mealType === 1 ? "Breakfast" : "Dinner"
    } monthly invoice summary - ${getLocationName(location)}`,
    subtitle: "Total charges and payment information",
    summary: { ...details },
    chargedPlateCount: chargedPlateCount,
    totalInvoice: totalInvoice,
  };

  // using the following Array to rnder the invoice cost details
  let InvoiceCostDetails = [
    { label: "Total no. of plate count", value: chargedPlateCount },
    { label: "Sub total", value: `₹ ${totalInvoice}` },
    { label: "Tax", value: "₹ 0.00" },
    {
      label: "Total including tax",
      value: `₹ ${totalInvoice}`,
      isBold: true,
    },
  ];

  //using the following useEffect to get the lessConsumption days from the total meals
  useEffect(() => {
    if (!invoiceDescriptionData) return;
    let lessConsumption = 0;
    const seenDates = new Set();

    invoiceDescriptionData.forEach((item) => {
      const dateKey = item.mealDate?.trim();
      if (dateKey && seenDates.has(dateKey)) return;
      if (dateKey) seenDates.add(dateKey);

      const totalMeal = item.details?.reduce(
        (acc, curr) => acc + (Number(curr.mealCount) || 0),
        0
      );
      if (totalMeal < 150) {
        lessConsumption += 1;
      }
    });

    setLessConsumptionDays(lessConsumption);
  }, [invoiceDescriptionData]);

  //using the following useEffect to render the moreConsumption days from the total meals.
  useEffect(() => {
    if (!invoiceDescriptionData) return;
    let moreConsumption = 0;
    const seenDates = new Set();

    invoiceDescriptionData.forEach((item) => {
      const dateKey = item.mealDate?.trim();
      if (dateKey && seenDates.has(dateKey)) return;
      if (dateKey) seenDates.add(dateKey);

      const totalMeal = item.details?.reduce(
        (acc, curr) => acc + (Number(curr.mealCount) || 0),
        0
      );
      if (totalMeal > 150) {
        moreConsumption += 1;
      }
    });

    setMoreConsumptionDays(moreConsumption);
  }, [invoiceDescriptionData]);

  useEffect(() => {
    setInvoiceSummaryData((prev) => ({
      ...prev,
      "Invoice Period": invoiceDate,
    }));
  }, [invoiceDate, setInvoiceSummaryData]);

  //uisng the following function to return the invoice firstDay and lastDay of the month
  function invoicePeriod(currMonth, year) {
    const date = moment(`${currMonth} ${year}`, "MMMM YYYY");
    const firstDay = date.clone().startOf("month").format("MMMM D");
    const lastDay = date.clone().endOf("month").format("MMMM D");
    return `${firstDay}-${lastDay},${year}`;
  }

  function handleTextTransform(vendorName) {
    let tranform = vendorName
      ?.toLowerCase()
      .split(" ")
      .map((word, index) =>
        index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word
      )
      .join(" ");
    return tranform ? tranform : "No provider available";
  }

  return (
    <Box
      p={1}
      sx={{ border: "1px solid #ddd", borderRadius: "8px", background: "#fff" }}
    >
      {/* Invoice Title */}
      <Typography variant="h6" fontWeight="bold">
        {invoiceDetails?.title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {invoiceDetails?.subtitle}
      </Typography>

      {/* Invoice Details */}
      <Grid
        container
        spacing={2}
        mt={2}
        sx={{ display: "flex", justifyContent: "space-between", border: "" }}
      >
        {mealType === 1 ? (
          <InvoiceBreakFastByLocation invoiceDetails={invoiceDetails} />
        ) : mealType === 2 && location === 1 ? (
          <InvoiceBreakFastByLocation invoiceDetails={invoiceDetails} />
        ) : mealType !== 1 && location !== 1 ? (
          <InvoiceDinnerByLocation
            invoiceDetails={invoiceDetails}
            invoiceSummaryData={invoiceSummaryData}
          />
        ) : (
          ""
        )}
      </Grid>
      <Divider sx={{ my: 2 }} />
      <Grid container spacing={10} alignItems="center">
        <Grid item xs={9}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#f8f9fa",
                  borderRadius: "8px",
                  minHeight: "100px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="body2" fontWeight="bold">
                  No. of working days
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {Array.isArray(invoiceDescriptionData) &&
                  invoiceDescriptionData.length > 0
                    ? (() => {
                        const uniqueDates = new Set(
                          invoiceDescriptionData.map((item) => item.mealDate)
                        );
                        return uniqueDates.size;
                      })()
                    : 0}{" "}
                  days
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={4}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#f8f9fa",
                  borderRadius: "8px",
                  minHeight: "100px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="body2" fontWeight="bold">
                  No. of days consumption was less than min guarantee
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {`${lessConsumptionDays} days`}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={4}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#f8f9fa",
                  borderRadius: "8px",
                  minHeight: "100px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="body2" fontWeight="bold">
                  No. of days consumption was greater than min guarantee
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {`${moreConsumptionDays} days`}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={3}>
          {InvoiceCostDetails.map((cost, i) => {
            return (
              <Box
                sx={{
                  paddingBottom: "10px",
                  display: "flex",
                  justifyContent: "space-between",
                  borderBottom:
                    i === InvoiceCostDetails.length - 1
                      ? "1px solid #C1C1C2"
                      : "",
                }}
              >
                <Typography variant="body2">{cost.label}</Typography>
                <Typography
                  variant="body2"
                  fontWeight={cost.isBold ? "bold" : "normal"}
                >
                  {cost.value}
                </Typography>
              </Box>
            );
          })}
          <Typography variant="h6" fontWeight="bold" mt={2} textAlign="right">
            Grand total: {`₹ ${totalInvoice}`}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

export default InvoiceSummary;
